import React, { useContext, useMemo } from "react";
import { Pagination } from "@design-system/Pagination/Pagination";
import { CommandIsland } from "@design-system/CommandIsland/CommandIsland";
import commandIslandStyles from "@design-system/CommandIsland/CommandIslandVariants.module.scss";
import { Button } from "@design-system/Button/Button";
import { WithHotkey } from "@design-system/Button/WithHotkey";
import { Tooltip } from "@design-system/Tooltip/Tooltip";
import { Shortcut } from "@design-system/Shortcut/Shortcut";
import { TaskType } from "@common/types/task";
import { useOverlays } from "contexts/OverlayContext";
import { useViewOnlyRefusal } from "@hooks/permissions/useViewOnlyRefusal";
import { useIsGlFirst } from "@hooks/useIsGlFirst";
import { useEntities } from "@hooks/useEntities";
import { useScope } from "@state/scopeContext.lib";
import {
    TransactionColumn,
    TransactionView,
} from "@common/types/views/transactionViews";
import { TransactionReviewField } from "@common/types/transactionFieldReview";
import { usePaginationWithBulkReset } from "@components/transactions/hooks/usePaginationWithBulkReset";
import { CommandIslandTotal } from "@design-system/CommandIsland/CommandIslandTotal";
import { pluralize } from "@common/helpers/string";
import { HStack } from "@design-system/HStack/HStack";
import { useTransactionBulkActions } from "../TransactionsBulkActions/useTransactionBulkActions";
import { TransactionsBulkModals } from "../TransactionsBulkActions/TransactionsBulkModals";
import {
    PaginatedTransactionsActionsContext,
    PaginatedTransactionsDataContext,
} from "../PaginatedTransactionsTable.context";
import { ReviewSelectedButton } from "./ReviewSelectedButton";
import { UnreviewSelectedButton } from "./UnreviewSelectedButton";
import { TransactionsTaskAskButton } from "@components/tasks/TaskBody/Transactions/TransactionsTaskAskButton";

interface TransactionsViewFooterProps {
    scrollTo?: (options: ScrollToOptions) => void;
    view: TransactionView;
    isOrganizationMember?: boolean;
    reviewTransactionFields?: TransactionReviewField[];
    /** When "unreview", the bulk action button labels as Unreview and revokes reviews. */
    reviewMode?: "review" | "unreview";
}

export const TaskTransactionsViewFooter = ({
    view,
    scrollTo,
    isOrganizationMember,
    reviewTransactionFields,
    reviewMode,
}: TransactionsViewFooterProps) => {
    const {
        currentPage,
        totalPages,
        transactions,
        transactionCount: totalTransactionCount,
        loadMore,
    } = useContext(PaginatedTransactionsDataContext);
    const { setPage } = useContext(PaginatedTransactionsActionsContext);
    const { enabled: isGlFirst } = useIsGlFirst();

    const onPageChange = (newPage: number) => {
        setPage(newPage);
        scrollTo?.({ top: 0, behavior: "smooth" });
    };

    /**
     * Columns relevant to this task view, used to decide which
     * column-specific bulk actions to offer. Review-variant columns
     * (customVariants.variant === "review") still render their normal
     * editable cell content (e.g. CategoryCell, LedgerCell) plus a review
     * button — they are exactly the fields this review task is meant to
     * act on, so they must be included, not excluded.
     */
    const visibleColumnIds = useMemo(
        () => new Set(view.columns.map((c) => c.id)),
        [view.columns],
    );

    const txBulk = useTransactionBulkActions({ visibleColumnIds });
    const { openOverlay } = useOverlays();
    const { refuseIfViewOnly } = useViewOnlyRefusal();
    const { workspaceId } = useScope();
    const entities = useEntities();
    const fallbackEntityIds = useMemo(
        () => entities.map((entity) => entity.id),
        [entities],
    );

    const pageChangeWithBulkActionReset = usePaginationWithBulkReset({
        onPageChange,
    });

    const hasCategoryColumn = visibleColumnIds.has(TransactionColumn.CATEGORY);
    const hasLedgerColumn = visibleColumnIds.has(TransactionColumn.LEDGER);

    const isSelected = txBulk.selectedCount > 0;
    const hasPagination = !loadMore && totalPages > 1;

    const handleAskClient = () => {
        // Opens a create form the server can only refuse on save (PRO-11345).
        if (refuseIfViewOnly()) {
            return;
        }
        const entityIds = [
            ...new Set(
                txBulk.selectedArray.map(
                    (transaction) => transaction.entity.id,
                ),
            ),
        ];

        openOverlay("createTask", {
            type: TaskType.TRANSACTIONS,
            viewConfig: {
                ...view,
                filters: {
                    entityIds,
                    transactionIds: txBulk.selectedArray.map(
                        (transaction) => transaction.id,
                    ),
                },
            },
            transactionsTask: {
                transactionsCount: txBulk.selectedCount,
                transactions: txBulk.selectedArray,
            },
            initialValues: {
                entityIds,
            },
        });
    };

    if (!transactions || transactions.length === 0) {
        return null;
    }

    return (
        <>
            <CommandIsland
                sticky
                className={commandIslandStyles.bulkActionsStickyFooter}
            >
                <HStack gap={8}>
                    {hasPagination && (
                        <Pagination
                            size="md"
                            pageCount={totalPages}
                            currentPage={currentPage}
                            disabled={txBulk.isBusy}
                            onPageChange={pageChangeWithBulkActionReset}
                        />
                    )}
                    <CommandIslandTotal>
                        {totalTransactionCount}{" "}
                        {pluralize("transaction", totalTransactionCount)}
                    </CommandIslandTotal>

                    {isSelected && (
                        <>
                            <Tooltip
                                key={txBulk.selectedCount}
                                content={`Deselect ${txBulk.selectedCount}`}
                            >
                                <Button
                                    variant="primary"
                                    size="md"
                                    onClick={txBulk.clearSelection}
                                >
                                    {txBulk.selectedCount} selected
                                </Button>
                            </Tooltip>
                            {txBulk.showSelectAll && (
                                <Button
                                    variant="primary"
                                    size="md"
                                    onClick={txBulk.selectAll}
                                >
                                    Select all
                                </Button>
                            )}
                        </>
                    )}
                </HStack>

                {isSelected && (
                    <HStack gap={8}>
                        {hasCategoryColumn && !isGlFirst && !reviewMode && (
                            <WithHotkey
                                hotKey="c"
                                hotKeyOptions={{
                                    description: "Set category",
                                    preventDefault: true,
                                }}
                                tooltip={
                                    txBulk.lockedSelectionTooltip ??
                                    "Set category"
                                }
                            >
                                <Button
                                    variant="primary"
                                    size="md"
                                    onClick={txBulk.handleSetCategoryClick}
                                    disabled={txBulk.isFieldLocked}
                                >
                                    Set category
                                </Button>
                            </WithHotkey>
                        )}
                        {hasLedgerColumn &&
                            txBulk.shouldShowCoaButton &&
                            !reviewMode && (
                                <WithHotkey
                                    hotKey="g"
                                    hotKeyOptions={{
                                        description: "Cash ledger",
                                        preventDefault: true,
                                    }}
                                    tooltip={
                                        txBulk.lockedSelectionTooltip ??
                                        "Set cash ledger"
                                    }
                                >
                                    <Button
                                        variant="primary"
                                        size="md"
                                        onClick={txBulk.handleSetCoaClick}
                                        disabled={txBulk.isFieldLocked}
                                    >
                                        Set cash ledger
                                    </Button>
                                </WithHotkey>
                            )}

                        {isOrganizationMember && !reviewMode && (
                            <Button
                                variant="primary"
                                size="md"
                                onClick={handleAskClient}
                            >
                                Ask client
                            </Button>
                        )}
                        <TransactionsTaskAskButton />
                        <Tooltip content="Open bulk actions">
                            <Button
                                variant="primary"
                                size="md"
                                onClick={txBulk.handleOpenBulkActions}
                                disabled={txBulk.isBusy}
                                rightIcon={
                                    <span data-slot-box="">
                                        <Shortcut keys="mod+k" />
                                    </span>
                                }
                            >
                                Actions
                            </Button>
                        </Tooltip>
                        {reviewTransactionFields &&
                            reviewTransactionFields.length > 0 &&
                            (reviewMode === "unreview" ? (
                                <UnreviewSelectedButton
                                    reviewTransactionFields={
                                        reviewTransactionFields
                                    }
                                    selectedTransactions={txBulk.selectedArray}
                                    onClearSelection={txBulk.clearSelection}
                                    hasSelectedAll={txBulk.hasSelectedAll}
                                    totalTransactions={txBulk.totalTransactions}
                                    filtersSelection={txBulk.filtersSelection}
                                    workspaceId={workspaceId}
                                    fallbackEntityIds={fallbackEntityIds}
                                />
                            ) : (
                                <ReviewSelectedButton
                                    reviewTransactionFields={
                                        reviewTransactionFields
                                    }
                                    selectedTransactions={txBulk.selectedArray}
                                    onClearSelection={txBulk.clearSelection}
                                    hasSelectedAll={txBulk.hasSelectedAll}
                                    totalTransactions={txBulk.totalTransactions}
                                    filtersSelection={txBulk.filtersSelection}
                                    workspaceId={workspaceId}
                                    fallbackEntityIds={fallbackEntityIds}
                                />
                            ))}
                    </HStack>
                )}
            </CommandIsland>

            <TransactionsBulkModals
                shouldShowUnmatch={txBulk.shouldShowUnmatch}
                closeUnmatch={() => txBulk.setShouldShowUnmatch(false)}
                shouldShowUnsplit={txBulk.shouldShowUnsplit}
                closeUnsplit={() => txBulk.setShouldShowUnsplit(false)}
                shouldShowRevertGL={txBulk.shouldShowRevertGL}
                closeRevertGL={() => txBulk.setShouldShowRevertGL(false)}
                confirmRevertGL={txBulk.confirmRevertGL}
                shouldShowRemoveCounterparty={
                    txBulk.shouldShowRemoveCounterparty
                }
                closeRemoveCounterparty={() =>
                    txBulk.setShouldShowRemoveCounterparty(false)
                }
                confirmRemoveCounterparty={txBulk.confirmRemoveCounterparty}
                shouldShowRemoveCustomer={txBulk.shouldShowRemoveCustomer}
                closeRemoveCustomer={() =>
                    txBulk.setShouldShowRemoveCustomer(false)
                }
                confirmRemoveCustomer={txBulk.confirmRemoveCustomer}
                shouldShowDelete={txBulk.shouldShowDelete}
                closeDelete={() => txBulk.setShouldShowDelete(false)}
                confirmDelete={txBulk.confirmDelete}
                shouldShowGusto={txBulk.shouldShowGusto}
                closeGusto={() => txBulk.setShouldShowGusto(false)}
                selectedArray={txBulk.selectedArray}
                scheduleConfirmModal={txBulk.scheduleConfirmModal}
                shouldShowCreateCounterparty={txBulk.showCreateCounterparty}
                closeCreateCounterparty={() =>
                    txBulk.setShowCreateCounterparty(false)
                }
                onCreateCounterpartyClosed={
                    txBulk.handleCreateCounterpartyClosed
                }
                shouldShowCreateCustomer={txBulk.showCreateCustomer}
                closeCreateCustomer={() => txBulk.setShowCreateCustomer(false)}
                onCreateCustomerClosed={txBulk.handleCreateCustomerClosed}
                shouldShowMemo={txBulk.shouldShowMemo}
                closeMemo={() => txBulk.setShouldShowMemo(false)}
                switchingToBusinessCategory={txBulk.switchingToBusinessCategory}
                hideBusinessSelection={txBulk.hideBusinessSelection}
                onBusinessSelected={txBulk.onBusinessSelected}
            />
        </>
    );
};
